import React, { useState } from 'react';
import classnames from 'classnames';
import { ItemDelegate, GroupDelegate } from '@leverege/molten/imagine';
import ObjectUtil from '@leverege/object-util';
import P from '@leverege/promise';
import { DataSources } from '@leverege/ui-attributes';
import { Toast } from '@leverege/ui-elements';
import { GlobalState, Selection, UserSettings } from '@leverege/ui-redux';
import isEqual from 'lodash.isequal';
import BulkSaleInformation from '../screens/BulkSaleInformation';
import PendingSaleMessageQuestion from '../screens/PendingSaleMessageQuestion';
import PendingSaleMessage from '../screens/PendingSaleMessage';
import SearchRecipients from '../../../alerts/views/SearchRecipients';
import RecovrSales from '../../../util/RecovrSales';
import C from '../Constants';
import S from './BulkVehicleOnly.css';
import { jsx as _jsx } from "react/jsx-runtime";
const PENDING_SALE_MESSAGE_TITLE = 'Send SMS About This Sale';
const TITLES_BY_SALE_TYPE = {
  [C.SALE_TYPES.DEALER_TRADE]: 'Dealer Trade',
  [C.SALE_TYPES.VEHICLE_ONLY]: 'Vehicle Only Sale',
  [C.SALE_TYPES.VEHICLE_WHOLESALE]: 'Vehicle Wholesale'
};
export default function BulkVehicleOnly(props) {
  const {
    context,
    actions: pActions,
    vehicles,
    saleData,
    saleType,
    setLoadingMessage,
    onClose,
    onChange
  } = props;
  const state = useState({});
  const [currentStepId, setCurrentStepId] = useState('bulk-sale-information');
  const selectionLimit = 5;
  const goToStep = stepId => () => {
    setCurrentStepId(stepId);
  };
  const onSaleSubmit = async () => {
    setLoadingMessage('Completing Sale');

    // Run sell calls with a concurrency ceiling.
    const results = await P.mapLimit(vehicles, async vehicle => {
      try {
        const actions = pActions || DataSources.getActions(vehicle);
        const result = await RecovrSales.sell({
          ...saleData,
          actions,
          vehicle,
          hasCustomer: false,
          vehicleLeftLot: false,
          toast: false,
          saleType,
          goToSales: false,
          throwErr: true
        });
        return result;
      } catch (error) {
        // Easier to create a new error instance here than checking the status code when processing the results.
        return new Error(error.message);
      }
    }, {
      limit: 5
    });

    // Separate sell call results by outcome (success/failure).
    const successes = [];
    const partials = [];
    const failures = [];
    results.forEach((result, idx) => {
      if (result instanceof Error) {
        failures.push(vehicles[idx]);
      } else if (result.message.includes('pending')) {
        partials.push(vehicles[idx]);
      } else {
        successes.push(vehicles[idx]);
      }
    });
    const partialsPlural = partials.length === 1 ? 'vehicle' : 'vehicles';
    const failuresPlural = failures.length === 1 ? 'vehicle' : 'vehicles';
    const successesPlural = successes.length === 1 ? 'vehicle' : 'vehicles';

    // Display toast message for successful calls and unselect corresponding vehicles.
    if (successes.length > 0) {
      const {
        clientProps: {
          targetKey
        }
      } = context;
      GlobalState.dispatch(Selection.remove(targetKey, successes));
    }

    // If only one vehicle is sold and it has a locator, send user to pending sale message
    if (vehicles.length === 1 && partials.length === 1) {
      // Question user about pending sale message if in the present of a single vehicle sale and user w/o disabled pending sale message dialog.
      Toast.success('Successfully marked 1 vehicle as pending sale.');
      setLoadingMessage(null);
      state[1](s => ({
        ...s,
        pendingSaleMessageSaleId: results[0].saleId
      }));
      if (!UserSettings.get(GlobalState.get(), 'recovr.pendingSaleMessage', {
        returnPromise: true
      })?.value) {
        goToStep('pending-sale-message-question')();
      } else {
        onClose();
      }
    }
    // Accounts for if there are all successes and no failures, OR some partials and successes and no failures
    if ((partials.length === 0 || vehicles.length > 1) && failures.length === 0) {
      const delegate = context.clientProps.actions;
      if (delegate instanceof GroupDelegate) {
        setTimeout(() => GlobalState.dispatch(delegate.parent().child('sales').goTo()), 500);
      }
      if (delegate instanceof ItemDelegate) {
        setTimeout(() => GlobalState.dispatch(delegate.parent().parent().child('sales').goTo()), 500);
      }
      // Creates toast for only all successes, or a combined message for some partials and some successes. If no successes and multiple partials, then will only notify about partials.
      Toast.success(partials.length === 0 ? `Successfully marked ${successes.length} 
        ${successesPlural} as sold.` : `Successfully marked ${partials.length} 
        ${partialsPlural} as pending${successes.length ? ` and ${successes.length} 
        ${successesPlural} as sold.` : '.'}`);
      onClose();
    } else {
      // If there are any errors, will create new toast for only errors or errors with mixed partials/successes
      const reloadData = context.clientProps.reloadData;
      setTimeout(reloadData, 500);
      if (failures.length > 0) {
        Toast.error(vehicles.length === failures.length ? `Error marking ${failures.length} ${failuresPlural} as sold.` : `Error marking ${failures.length} out of ${vehicles.length} vehicles as sold or pending.`);
      }
      setLoadingMessage(null);
    }
  };
  const onPendingSaleMessageQuestionSubmit = () => {
    goToStep('pending-sale-message')();
  };
  const onPendingSaleMessageQuestionCancel = () => {
    if (state[0]?.pendingSaleMessageQuestionDisabled) {
      UserSettings.set('recovr.pendingSaleMessage', {
        value: true
      });
    }
    onClose();
  };
  const onPendingSaleMessageSubmit = async () => {
    const actions = pActions || DataSources.getActions(vehicles[0]);
    await RecovrSales.sendPendingSaleMessage({
      saleId: state[0].pendingSaleMessageSaleId,
      recipients: state[0].pendingSaleMessageRecipients.map(value => typeof value === 'object' ? ObjectUtil.pick(['id', 'username', 'email', 'phone'], value) : value),
      message: state[0].pendingSaleMessage,
      actions
    });
    onClose();
  };
  const onPendingSaleMessageUserListCancel = () => {
    goToStep('pending-sale-message')();
  };
  const onPendingSaleMessageUserListSubmit = () => {
    state[1](s => ({
      ...s,
      pendingSaleMessageRecipients: s.pendingSaleMessageRecipientsTmp
    }));
    goToStep('pending-sale-message')();
  };
  const onAddRecipient = () => {
    goToStep('pending-sale-message-user-list')();
  };
  const onRemoveRecipient = ({
    data
  }) => {
    state[1](s => ({
      ...s,
      pendingSaleMessageRecipients: s.pendingSaleMessageRecipients.filter(r => r !== data)
    }));
  };
  const steps = {
    'bulk-sale-information': {
      submitText: vehicles.length === 1 ? `Mark ${vehicles.length} Vehicle As Sold` : `Mark ${vehicles.length} Vehicles As Sold`,
      // eslint-disable-line max-len
      onSubmit: onSaleSubmit,
      content: () => /*#__PURE__*/_jsx(BulkSaleInformation, {
        ...saleData,
        saleType: saleType,
        vehicles: vehicles,
        onChange: onChange
      })
    },
    'pending-sale-message-question': {
      submitText: 'Yes, Send SMS',
      submitDisabled: state[0]?.pendingSaleMessageQuestionDisabled,
      cancelText: "No, I'm done",
      onSubmit: onPendingSaleMessageQuestionSubmit,
      onCancel: onPendingSaleMessageQuestionCancel,
      contentClassName: classnames(S.pendingSaleMessageQuestionContent),
      content: () => /*#__PURE__*/_jsx(PendingSaleMessageQuestion, {
        ...saleData,
        saleType: saleType,
        vehicles: vehicles,
        onChange: onChange,
        state: state
      })
    },
    'pending-sale-message': {
      submitText: 'Send SMS',
      submitDisabled: !state[0]?.pendingSaleMessageRecipients?.length,
      cancelText: 'Cancel',
      onSubmit: onPendingSaleMessageSubmit,
      onCancel: onClose,
      contentClassName: classnames(S.pendingSaleMessageContent),
      content: () => /*#__PURE__*/_jsx(PendingSaleMessage, {
        ...saleData,
        saleType: saleType,
        vehicles: vehicles,
        onChange: onChange,
        onAdd: onAddRecipient,
        onRemove: onRemoveRecipient,
        state: state
      })
    },
    'pending-sale-message-user-list': {
      submitText: 'Apply',
      onSubmit: onPendingSaleMessageUserListSubmit,
      submitDisabled: isEqual(state[0]?.pendingSaleMessageRecipientsTmp, state[0]?.pendingSaleMessageRecipients),
      cancelHidden: true,
      onCancel: onClose,
      headerClassName: classnames(S.headerClassName),
      contentClassName: classnames(S.pendingSaleMessageContent),
      content: () => /*#__PURE__*/_jsx(SearchRecipients, {
        ...saleData,
        actions: pActions,
        saleType: saleType,
        vehicles: vehicles,
        onChange: onChange,
        onCancel: onPendingSaleMessageUserListCancel,
        state: state,
        selectionLimit: selectionLimit,
        searchPhones: true,
        searchUsers: true
      })
    }
  };
  const title = currentStepId === 'pending-sale-message' ? PENDING_SALE_MESSAGE_TITLE : TITLES_BY_SALE_TYPE[saleType];
  const icon = null;
  const currentStep = steps[currentStepId] || {};
  return {
    title,
    icon,
    currentStep
  };
}